import React from 'react';
import { useTranslation } from 'react-i18next';
import { rowActivation } from './rowActivation';
import { Avatar, recency } from './rows';

/**
 * ContactCardRow (phone) + ContactGridRow (desktop table) — UXeng's Phase-2 Contacts spec
 * (fleet-reports/UXeng-phase2-per-screen.md §2): title `Name`, secondary `Title · Account` on one
 * truncated line, meta `Last Contacted`. ZERO pills — Email is not on the card, it lives in the panel;
 * a card is for recognising a person, not for reading their fields. `Account` stays plain text, never
 * a pill (it is a navigation target, not an option-identity attribute).
 *
 * `lastActivityAt` here is the SAME Lead field the existing Leads screen already shows as "Last
 * activity" (unchanged semantics — an admin edit can bump it, same as it always could). That is
 * DIFFERENT from cmdr's Deal.lastCustomerActivityAt concern: Deal would have BORROWED another
 * entity's gameable field to imply deal health; this is a Lead row showing its own field, on the
 * screen that already shows it. No new gaming surface.
 */
export interface ContactRow {
  id: string;
  leadId: string;
  name: string;
  /** B5/D-L5 — set ONLY for a nameless token-keyed contact: the anchor token, demoted to muted
   *  secondary text beside "Unnamed contact" so the row stays traceable without a hex identity. */
  token?: string | null;
  email?: string | null;
  jobTitle?: string | null;
  companyName?: string | null;
  lastActivityAt?: string | null;
}

export interface ContactRowCallbacks {
  selected?: boolean;
  onOpenPerson: (contact: ContactRow) => void;
}

function titleAccount(c: ContactRow, t: (k: string) => string): string {
  const parts = [c.jobTitle, c.companyName].filter(Boolean);
  return parts.length ? parts.join(' · ') : t('contactsGrid.noCompanyOrTitle');
}

export const ContactCardRow: React.FC<{ contact: ContactRow } & ContactRowCallbacks> = ({ contact, selected, onOpenPerson }) => {
  const { t } = useTranslation('common');
  return (
    <div {...rowActivation(() => onOpenPerson(contact))} onClick={() => onOpenPerson(contact)}
      className={`flex gap-3 p-3 rounded-xl bg-surface border ${selected ? 'ring-2 ring-primary/15 border-primary/30' : 'border-border-neutral'} cursor-pointer`}>
      <Avatar lead={{ id: contact.id, leadId: contact.leadId, name: contact.name }} size={38} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-text-strong truncate">{contact.name || '—'}</span>
          {contact.token && <span data-testid="contact-token-secondary" className="text-[11px] text-text-muted shrink-0" dir="ltr">{contact.token}</span>}
          <span className="ms-auto text-[11px] text-text-muted shrink-0" dir="ltr">{recency(contact.lastActivityAt, t)}</span>
        </div>
        <div className="text-xs text-text-muted truncate">{titleAccount(contact, t)}</div>
      </div>
    </div>
  );
};

export const ContactGridRow: React.FC<{ contact: ContactRow } & ContactRowCallbacks> = ({ contact, selected, onOpenPerson }) => {
  const { t } = useTranslation('common');
  return (
    <tr tabIndex={0} onKeyDown={rowActivation(() => onOpenPerson(contact)).onKeyDown} className={`border-b border-border-neutral hover:bg-canvas cursor-pointer ${selected ? 'bg-canvas' : ''}`} onClick={() => onOpenPerson(contact)}>
      <td className="sticky z-10 bg-surface p-cell-y px-cell-x [inset-inline-start:0]">
        <div className="flex items-center gap-2 min-w-0"><Avatar lead={{ id: contact.id, leadId: contact.leadId, name: contact.name }} size={28} /><span className="text-sm font-semibold text-text-strong truncate">{contact.name || '—'}</span>{contact.token && <span data-testid="contact-token-secondary" className="text-[11px] text-text-muted shrink-0" dir="ltr">{contact.token}</span>}</div>
      </td>
      <td className="p-cell-y px-cell-x text-sm text-text-strong whitespace-nowrap truncate">{contact.companyName || '—'}</td>
      <td className="p-cell-y px-cell-x text-sm text-text-muted whitespace-nowrap truncate">{contact.jobTitle || '—'}</td>
      <td className="p-cell-y px-cell-x text-xs text-text-muted whitespace-nowrap" dir="ltr">{contact.email || '—'}</td>
      <td className="p-cell-y px-cell-x text-[11px] text-text-muted whitespace-nowrap" dir="ltr" title={contact.lastActivityAt ?? undefined}>{recency(contact.lastActivityAt, t)}</td>
    </tr>
  );
};
